// Account Reports -> Contacts per Order (2026-10-27, decided with Patrick).
// Each cell leads with CPO (contacts / orders, two decimals like Power BI),
// with contacts / orders and Cancel % under it. Agent or Total CPO by toggle:
// Total counts every customer contact, Agent leaves out what CXHub automation
// resolved. Orders are gross (cancelled kept). Each row is one company +
// channel, never combined; TRQ's two group rows carry the contacts Bright
// Pattern does not split by vendor (Sales Support is a reference against
// Amazon DF only). The chart plots CPO over time for the company totals, or
// for the chosen channels when the Channel filter is narrowed.
import { useEffect, useMemo, useState } from 'react'
import {
  Alert, Box, Checkbox, Chip, CircularProgress, FormControl, InputLabel, ListItemText, MenuItem, Paper, Select, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import { fetchCpoReport } from '../api.js'
import LineChart from '../LineChart.jsx'

const COMPANIES = ['1A', 'AM', 'TRQ']
const GRANULARITIES = [
  { value: 'week', label: 'Weekly' },
  { value: 'day', label: 'Daily' },
  { value: 'month', label: 'Monthly' },
]
// A fixed color per line, so a filter never repaints a survivor.
const COLORS = {
  '1A': '#1976d2', AM: '#9c27b0', TRQ: '#2e7d32',
  '1AE': '#1976d2', '1AW': '#0288d1', '1AA': '#ed6c02',
  AME: '#9c27b0', AMW: '#7b1fa2', AMA: '#f57c00',
  WMT: '#2e7d32', TRQ_ORDER_SUPPORT: '#558b2f', TRQ_SALES_SUPPORT: '#c2185b',
}

const cpoOf = (b, measure) => (b && b[measure] != null && b.orders ? b[measure] / b.orders : null)

function Cell({ bucket, expectedDays, measure, bold, first }) {
  const edge = first ? { borderLeft: 1, borderColor: 'divider' } : {}
  if (!bucket) return <TableCell align="center" sx={{ color: 'text.disabled', ...edge }}>—</TableCell>
  const pct = bucket.orders ? (100 * bucket.cancelled) / bucket.orders : null
  const partial = expectedDays && bucket.days < expectedDays ? '*' : ''
  const cpo = cpoOf(bucket, measure)
  const contacts = bucket[measure]
  return (
    <TableCell align="center" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', verticalAlign: 'top', ...edge }}>
      {contacts != null ? (
        <Typography sx={{ fontWeight: bold ? 800 : 700, fontSize: '1rem', lineHeight: 1.2 }}>{cpo == null ? '—' : cpo.toFixed(2)}{partial}</Typography>
      ) : (
        // Orders-only columns (no contacts to attribute) keep the CPO line's
        // height so every cell in the row lines up.
        <Typography sx={{ fontSize: '1rem', lineHeight: 1.2, color: 'text.disabled' }}>—</Typography>
      )}
      <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.3 }}>
        {contacts != null ? `${contacts.toLocaleString()} / ` : ''}{bucket.orders.toLocaleString()}{contacts == null ? partial : ''}
        {pct == null ? '' : ` · ${pct.toFixed(1)}%`}
      </Typography>
    </TableCell>
  )
}

export default function ContactsPerOrder() {
  const [granularity, setGranularity] = useState('week')
  const [measure, setMeasure] = useState('total') // 'total' | 'agent'
  const [companies, setCompanies] = useState(COMPANIES)
  const [channels, setChannels] = useState(null) // null = every channel
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    fetchCpoReport({ granularity })
      .then((r) => { if (!cancelled) setData(r) })
      .catch((e) => { if (!cancelled) setError(e.message) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [granularity])

  const toggleCompany = (c) => setCompanies((cur) => (cur.includes(c) ? (cur.length === 1 ? cur : cur.filter((x) => x !== c)) : COMPANIES.filter((x) => cur.includes(x) || x === c)))

  const labels = useMemo(() => [...(data?.labels ?? [])].reverse(), [data])
  // Channel names in catalog order, once each (Amazon covers 1A and AM Amazon).
  const channelOptions = useMemo(() => [...new Set((data?.rows ?? []).map((r) => r.channel))], [data])
  const visibleRows = (data?.rows ?? []).filter((r) => companies.includes(r.company) && (channels == null || channels.includes(r.channel)))
  const channelFiltered = channels != null && channels.length < channelOptions.length
  // Company totals over the rows shown: orders from channel rows only (a TRQ
  // group row repeats its vendors' orders), contacts from every row.
  const totalFor = (company, label) => {
    const rows = visibleRows.filter((r) => r.company === company && r.buckets[label])
    if (!rows.length) return null
    const t = { orders: 0, cancelled: 0, days: 0, total: null, agent: null }
    for (const r of rows) {
      const b = r.buckets[label]
      if (!r.group) { t.orders += b.orders; t.cancelled += b.cancelled; t.days = Math.max(t.days, b.days) }
      if (b.total != null) { t.total = (t.total ?? 0) + b.total; t.agent = (t.agent ?? 0) + b.agent }
    }
    return t
  }
  const shownCompanies = COMPANIES.filter((c) => companies.includes(c) && visibleRows.some((r) => r.company === c))

  // One column group per company: its total, then each shown channel.
  const columns = shownCompanies.map((company) => ({
    company,
    cols: [
      { key: `${company}-total`, title: channelFiltered ? 'Total (selected)' : 'Total', total: true, bucketOf: (l) => totalFor(company, l) },
      ...visibleRows.filter((r) => r.company === company)
        // TRQ's channels run alphabetically (Patrick, 2026-10-27); 1A and AM keep Website, eBay, Amazon.
        .sort((a, b) => (company === 'TRQ' ? a.channel.localeCompare(b.channel, 'en', { sensitivity: 'base' }) : 0))
        .map((r) => ({ key: r.code, title: r.group ? r.channel : `${r.channel} ${r.code}`, row: r, bucketOf: (l) => r.buckets[l] })),
    ],
  }))
  // The period's average: pooled (all contacts / all orders), so a light
  // week weighs less than a busy one.
  const pooled = (buckets) => {
    const present = buckets.filter(Boolean)
    if (!present.length) return null
    const t = { orders: 0, cancelled: 0, days: 0, total: null, agent: null }
    for (const b of present) {
      t.orders += b.orders
      t.cancelled += b.cancelled
      if (b.total != null) { t.total = (t.total ?? 0) + b.total; t.agent = (t.agent ?? 0) + b.agent }
    }
    return t
  }

  const series = useMemo(() => {
    if (!data) return []
    const fmt = (key, label, bucketOf) => {
      const points = {}
      for (const l of labels) { const v = cpoOf(bucketOf(l), measure); if (v != null) points[l] = v }
      const avg = pooled(labels.map((l) => bucketOf(l)))
      return { key, label, color: COLORS[key], points, average: cpoOf(avg, measure) }
    }
    if (channelFiltered) {
      return visibleRows.filter((r) => Object.values(r.buckets).some((b) => b.total != null))
        .map((r) => fmt(r.code, `${r.company} ${r.channel}`, (l) => r.buckets[l]))
    }
    return shownCompanies.map((c) => fmt(c, `${c} total`, (l) => totalFor(c, l)))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, labels, measure, companies, channels])

  return (
    <Box id="section-cpo" sx={{ scrollMarginTop: '112px', mt: 4 }}>
      <Typography variant="subtitle1" sx={{ mb: 0.5 }}>Contacts per Order</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: 980 }}>
        CPO = customer contacts ÷ orders placed, per company and channel. Orders come from TRQ with cancelled orders kept;
        Cancel % is the share cancelled since. Contacts are CXHub (eBay, Website email, Walmart, Sales Hold) plus Bright
        Pattern (phone, chat, Amazon email; Website email before the move to CXHub). Total counts every customer contact;
        Agent leaves out what CXHub automation resolved. Support mailboxes, internal notices and spam are left out.
        All 1A phone and chat counts toward 1A Website, so 1A Website runs a little high and 1A eBay a little low.
        TRQ Sales Support is a reference only: it also answers Amazon AFN buyers and TRQ brand questions, whose orders
        TRQ does not hold. Walmart rolls up under TRQ. * marks a period not fully counted yet.
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>Company:</Typography>
        {COMPANIES.map((c) => (
          <Chip key={c} size="small" label={c} color={companies.includes(c) ? 'primary' : 'default'}
            variant={companies.includes(c) ? 'filled' : 'outlined'} onClick={() => toggleCompany(c)} />
        ))}
        <FormControl size="small" sx={{ minWidth: 200, ml: 2 }}>
          <InputLabel>Channel</InputLabel>
          <Select
            multiple
            label="Channel"
            value={channels ?? channelOptions}
            onChange={(e) => {
              const v = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
              // The two action rows carry marker values; act on them, never keep them.
              if (v.includes('__all')) return setChannels(null)
              if (v.includes('__none')) return setChannels([])
              setChannels(v.length === channelOptions.length ? null : v)
            }}
            renderValue={(sel) => (channels == null ? 'All channels' : sel.length === 0 ? 'No channels' : sel.length === 1 ? sel[0] : `${sel.length} channels`)}
          >
            <MenuItem value="__all" dense><ListItemText primary="Select all" primaryTypographyProps={{ color: 'primary', fontWeight: 600 }} /></MenuItem>
            <MenuItem value="__none" dense divider><ListItemText primary="Deselect all" primaryTypographyProps={{ color: 'primary', fontWeight: 600 }} /></MenuItem>
            {channelOptions.map((c) => (
              <MenuItem key={c} value={c}>
                <Checkbox size="small" checked={channels == null || channels.includes(c)} />
                <ListItemText primary={c} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Period</InputLabel>
          <Select label="Period" value={granularity} onChange={(e) => setGranularity(e.target.value)}>
            {GRANULARITIES.map((g) => <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>)}
          </Select>
        </FormControl>
        <ToggleButtonGroup size="small" exclusive value={measure} onChange={(_e, v) => v && setMeasure(v)} sx={{ ml: 1 }}>
          <ToggleButton value="total">Total CPO</ToggleButton>
          <ToggleButton value="agent">Agent CPO</ToggleButton>
        </ToggleButtonGroup>
        {loading && <CircularProgress size={20} />}
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {data?.bpError && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>Bright Pattern contacts could not be read from Schedule Hub ({data.bpError}), so phone, chat and Amazon email are missing and CPO reads low.</Alert>
      )}
      {data?.trq?.failed > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>TRQ did not answer {data.trq.failed} order count{data.trq.failed === 1 ? '' : 's'}; those cells show — . Reload to try again.</Alert>
      )}
      {data && channels?.length === 0 && (
        <Alert severity="info" sx={{ mb: 1.5 }}>No channels selected. Pick one or more in the Channel list.</Alert>
      )}

      {data && shownCompanies.length > 0 && (
        <Paper variant="outlined" sx={{ overflowX: 'auto', maxWidth: '100%' }}>
          <Table size="small" sx={{ width: 'auto' }}>
            <TableHead>
              <TableRow>
                <TableCell rowSpan={2} sx={{ verticalAlign: 'bottom', whiteSpace: 'nowrap' }}>{granularity === 'week' ? 'Week ending' : granularity === 'month' ? 'Month' : 'Day'}</TableCell>
                {columns.map((g) => (
                  <TableCell key={g.company} colSpan={g.cols.length} align="center" sx={{ fontWeight: 700, borderLeft: 1, borderColor: 'divider' }}>{g.company}</TableCell>
                ))}
              </TableRow>
              <TableRow>
                {columns.flatMap((g) => g.cols.map((c, i) => (
                  <TableCell key={c.key} align="center" sx={{ whiteSpace: 'nowrap', fontWeight: c.total ? 700 : 500, fontStyle: c.row?.group ? 'italic' : 'normal', borderLeft: i === 0 ? 1 : 0, borderColor: 'divider', verticalAlign: 'bottom' }}>
                    {c.title}
                    {c.row?.reference && <Typography variant="caption" color="text.secondary" component="div">reference only</Typography>}
                  </TableCell>
                )))}
              </TableRow>
            </TableHead>
            <TableBody>
              {labels.map((l) => (
                <TableRow key={l} hover>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{granularity === 'month' ? l : l.slice(5)}</TableCell>
                  {columns.flatMap((g) => g.cols.map((c, i) => (
                    <Cell key={c.key} bucket={c.bucketOf(l)} expectedDays={data.daysInBucket[l]} measure={measure} bold={c.total} first={i === 0} />
                  )))}
                </TableRow>
              ))}
              <TableRow sx={{ bgcolor: 'action.hover', '& td': { borderTop: 2, borderTopColor: 'divider' } }}>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Average ({labels.length} {granularity === 'week' ? 'weeks' : granularity === 'month' ? 'months' : 'days'})</TableCell>
                {columns.flatMap((g) => g.cols.map((c, i) => (
                  <Cell key={c.key} bucket={pooled(labels.map((l) => c.bucketOf(l)))} measure={measure} bold first={i === 0} />
                )))}
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      )}
      {data && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
          Each cell: CPO, then contacts / orders · Cancel %. The average is pooled: all contacts ÷ all orders for the periods shown.
          Orders are asked from TRQ when the report opens ({data.trq?.calls ?? 0} TRQ calls this time, {data.trq?.cached ?? 0} from memory), so a first load can take a few seconds. Daily shows the last 14 days.
        </Typography>
      )}
      {data && series.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2, maxWidth: 980 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            {measure === 'total' ? 'Total' : 'Agent'} CPO over time{channelFiltered ? ' — selected channels' : ' — company totals'}
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {series.map((sr) => (
              <Stack key={sr.key} direction="row" spacing={0.75} alignItems="center">
                <Box sx={{ width: 16, height: 3, borderRadius: 1, bgcolor: sr.color }} />
                <Typography variant="caption" color="text.primary">{sr.label}</Typography>
                {sr.average != null && <Typography variant="caption" color="text.secondary">avg {sr.average.toFixed(2)}</Typography>}
              </Stack>
            ))}
          </Stack>
          <LineChart dates={labels} series={series} valueFmt={(v) => v.toFixed(2)} height={260} responsive />
        </Paper>
      )}

    </Box>
  )
}
